import { NextStepViewport, useNextStep } from 'nextstepjs';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    FormLabel,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import Counter from 'src/components/fragments/Counter';
import { PlannedCareResolutionTypes } from 'src/constants/ResolutionTypes';
import {
    AlertTrainingElementIds,
    AlertTrainingTours,
    AlertTrainingViewportIds,
} from 'src/constants/Training';
import useAlertTraining from 'src/hooks/useAlertTraining';
import useFacilityFeatures from 'src/hooks/useFacilityFeatures';
import useFacilityGroups from 'src/hooks/useFacilityGroups';
import useTaskProperties from 'src/hooks/useTaskProperties';
import { selectEnterpriseId, selectFacilityId, selectStaffMemberId } from 'src/store/configSlice';
import {
    AlertOutcomeType,
    CareOutcome,
    CreateUnplannedCareTask,
    Resident,
    ResidentActivityUpdatePlannedCareTask,
    ResolveTaskRequest,
    ResolveTaskUpdatePlannedCareTask,
    Task,
    sageAdminApi,
    useCreateResidentActivityMutation,
    useGetResidentQuery,
    useResolveTaskMutation,
} from 'src/store/sageAdminApi';
import { ObservationType } from 'src/store/sageCareServiceApi';
import { buildPccTaskFollowUpAnswers } from 'src/utils/ehrUtils';
import { getResidentDisplayName } from 'src/utils/residentUtils';
import { snackActions } from 'src/utils/snackBarTools';

const CharacterLimit = 255;

export interface CareTaskRequestData {
    unplannedCareTask?: CreateUnplannedCareTask[];
    // The payloads for updating planned care differs based on method
    plannedCareTasksResidentActivity?: ResidentActivityUpdatePlannedCareTask[];
    plannedCareTasksResolveTask?: ResolveTaskUpdatePlannedCareTask[];
}
export interface ResolveTaskDrawerProps extends DrawerComponentProps {
    analyticsPrefix?: string;
    autoSubmit?: boolean;
    bypassCareConfirmationChecks?: boolean;
    isEmergency?: boolean;
    residentId?: string;
    resident?: Resident;
    careOutcomes?: CareOutcome[];
    observations?: ObservationType[];
    observationValues?: Record<string, string>;
    observationNotes?: Record<string, string>;
    alertOutcomeType?: AlertOutcomeType;
    showCancelAction?: boolean;
    submitButtonLabel?: string;
    successMessage?: string;
    isCareRefusal?: boolean;
    careTaskRequestData?: CareTaskRequestData;
    task?: Task;
}

function ResolveTaskDrawer(props: ResolveTaskDrawerProps) {
    const {
        title = 'Additional Notes',
        analyticsPrefix = title,
        autoSubmit = false,
        bypassCareConfirmationChecks = false,
        hasPreviousDrawer,
        isEmergency,
        onClose,
        onNext,
        onPrevious,
        residentId,
        resident,
        careOutcomes,
        observations = [],
        observationValues,
        observationNotes,
        alertOutcomeType,
        showCancelAction = true,
        submitButtonLabel = 'Submit',
        successMessage,
        isCareRefusal = false,
        // Empty in the case of logging care outside an Alert. The Task is created after the ResidentAcivity is created
        task = {},
        careTaskRequestData = {},
    } = props;

    const { isTrainingAlert, isGuidedTour, resolveTrainingAlert } = useAlertTraining({
        taskRid: task?.id,
    });

    const { startNextStep } = useNextStep();

    const { spacing } = useTheme();

    const { trackEvent, trackDrawerView } = useAnalytics();

    const enterpriseId = useSelector(selectEnterpriseId);
    const facilityId = useSelector(selectFacilityId);
    const staffMemberId = useSelector(selectStaffMemberId);

    const { careGroup } = useFacilityGroups();
    const { isUsingCommuniCareResolutionFlow } = useFacilityFeatures();

    const { data: residentFromServer, isLoading: isLoadingResident } = useGetResidentQuery(
        { residentId: residentId || resident?.id || '' },
        { skip: !!resident?.unit?.id || !(residentId || resident?.id) }
    );

    const residentToUse = resident || residentFromServer;
    const residentName = getResidentDisplayName(residentToUse);
    const unitId = resident?.unit?.id ?? residentFromServer?.unit?.id;

    const [createResidentActivity, { isLoading: isCreatingResidentActivity }] =
        useCreateResidentActivityMutation();
    const [resolveTask, { isLoading: isResolvingTask }] = useResolveTaskMutation();

    const dispatch = useDispatch();

    const isLoggingCare = !task || !task.id;
    const shouldDisableActions =
        isLoadingResident ||
        isResolvingTask ||
        isCreatingResidentActivity ||
        (isLoggingCare && !residentToUse);

    const { hasOnlyOtherCare, hasOtherCare } = useMemo(() => {
        const otherCareCount =
            // TODO(@robsli): check with product about expanding this requirement to all "_OTHER" fields
            careOutcomes?.filter(
                ({ careProvided }) => careProvided === PlannedCareResolutionTypes.OtherSafety.id
            ).length ?? 0;
        return {
            hasOnlyOtherCare: otherCareCount > 0 && otherCareCount === careOutcomes?.length,
            hasOtherCare: otherCareCount > 0,
        };
    }, [careOutcomes]);
    const shouldRequireNote = observations.some((obs) => obs === 'OTHER') || hasOnlyOtherCare;
    const shouldPromptForNote = !shouldRequireNote && hasOtherCare;

    const [notes, setNotes] = useState('');
    const [counter, setCounter] = useState(1);
    const isOverCharacterLimit = (notes?.length ?? 0) > CharacterLimit;
    const difference = Math.abs((notes?.length ?? 0) - CharacterLimit);

    const { isDoorAlarm } = useTaskProperties(task);

    const shouldShowStaffAssisted =
        !isDoorAlarm &&
        !isUsingCommuniCareResolutionFlow &&
        ((isLoggingCare && !isCareRefusal) || isEmergency);

    const getDefaultMessage = () => {
        if (isLoggingCare) {
            if (isCareRefusal) return 'Refused care logged successfully';
            return `Care logged for ${residentName}`;
        }
        if (isDoorAlarm) return 'Door Checked';
        if (isEmergency) return 'Alert Resolved';
        return 'Task Resolved';
    };

    const messageToDisplay = successMessage ?? getDefaultMessage();

    const handleGoBack = () => {
        trackEvent('Go Back Clicked', {
            objectType: ObjectTypes.Button,
            buttonLabel: 'Go Back',
            context: analyticsPrefix,
        });

        // TODO(@robsli): look into revamping this logic to make it less error-prone!
        // prevents the current resolutionType from overriding a new selection (see DrawerManager.handlePreviousDrawer)
        const propsToOmit = [
            'careOutcomes',
            'alertOutcomeType',
            'observations',
            'observationValues',
            'observationNotes',
            'submitButtonLabel',
            'successMessage',
            'title',
        ];
        onPrevious(true, propsToOmit);
    };

    const handleSuccess = (message: string) => {
        snackActions.success(message);
        onNext();
    };

    const handleError = (
        error: any,
        toastMessage = 'There was an error resolving this task. Please try again.',
        plannedCareTaskIds: string[] = [],
        severity: 'error' | 'warn' = 'error'
    ) => {
        const context = {
            taskId: task?.id,
            residentId: residentToUse?.id,
            plannedCareTaskIds: plannedCareTaskIds,
        };
        if (severity === 'warn') {
            datadogLogs.logger.warn('Error resolving task', context, error);
            snackActions.warning(toastMessage);
        } else {
            datadogRum.addError(error, context);
            snackActions.error(toastMessage);
        }
    };

    const addResidentActivity = async () => {
        try {
            const pccTaskFollowUpAnswers = isUsingCommuniCareResolutionFlow
                ? buildPccTaskFollowUpAnswers(careOutcomes || [])
                : undefined;

            const { rid: taskId } = await createResidentActivity({
                createResidentActivityRequest: {
                    creatorId: staffMemberId,
                    enterpriseId,
                    facilityId,
                    groupId: careGroup?.id ?? '',
                    residentId: residentToUse?.id!,
                    careOutcomes: careOutcomes || [],
                    observations: observations.map((obs) => ({
                        observationType: obs,
                        observationValue: observationValues?.[obs],
                        observationNote: observationNotes?.[obs],
                    })),
                    unitId,
                    resolutionNote: notes,
                    respondingStaffRids: Array.from({ length: counter }, () => staffMemberId),
                    createUnplannedCareTasks: careTaskRequestData.unplannedCareTask || [],
                    updatePlannedCareTasks:
                        careTaskRequestData.plannedCareTasksResidentActivity || [],
                    pccTaskFollowUpAnswers,
                },
            }).unwrap();

            trackEvent('Resident Activity Added', {
                taskId: taskId,
                objectType: ObjectTypes.Button,
                notesLength: notes?.length || 0,
                buttonLabel: submitButtonLabel,
                context: analyticsPrefix,
            });
            handleSuccess(messageToDisplay);
        } catch (error: any) {
            // At the moment, a 409 is only returned if ALL Planned Care in updatePlannedCareTasks have been charted OR deleted since data was fetched.
            if (error?.data?.code === 409) {
                handleError(
                    error,
                    `Documentation could not be saved. Please review your entries and resubmit if needed`,
                    careTaskRequestData.plannedCareTasksResidentActivity?.map((task) => task.id),
                    'warn'
                );
                dispatch(sageAdminApi.util.invalidateTags(['careTask']));
                onClose();
            } else {
                handleError(
                    error,
                    'There was an error reporting this emergency. Please try again.'
                );
                if (autoSubmit) hasPreviousDrawer ? handleGoBack() : onClose();
            }
        }
    };

    const handleResolveTask = async () => {
        try {
            if (isTrainingAlert) {
                resolveTrainingAlert(careOutcomes);
            } else {
                const pccTaskFollowUpAnswers = isUsingCommuniCareResolutionFlow
                    ? buildPccTaskFollowUpAnswers(careOutcomes || [])
                    : undefined;

                const resolveTaskRequest: ResolveTaskRequest = {
                    alertOutcomeType: alertOutcomeType,
                    resolutionNote: notes,
                    residentId: residentToUse?.id,
                    careOutcomes,
                    observations: observations.map((obs) => ({
                        observationType: obs,
                        observationValue: observationValues?.[obs],
                        observationNote: observationNotes?.[obs],
                    })),
                    bypassCareConfirmationChecks,
                    respondingStaffRids: Array.from({ length: counter }, () => staffMemberId),
                    createUnplannedCareTasks: careTaskRequestData.unplannedCareTask || [],
                    updatePlannedCareTasks: careTaskRequestData.plannedCareTasksResolveTask || [],
                    pccTaskFollowUpAnswers,
                };

                await resolveTask({ taskId: task?.id!, resolveTaskRequest }).unwrap();
            }

            const eventName = isEmergency ? 'Alert Resolved' : 'Task Resolved';
            trackEvent(eventName, {
                objectType: ObjectTypes.Button,
                notesLength: notes?.length || 0,
                buttonLabel: submitButtonLabel,
                context: analyticsPrefix,
                isTrainingAlert,
                isGuidedTour,
            });

            handleSuccess(messageToDisplay);
        } catch (error: any) {
            if (error?.data?.code === 409) {
                snackActions.error(
                    `The ${isEmergency ? 'alert' : 'task'} status has changed, please try again.`
                );
                dispatch(sageAdminApi.util.invalidateTags(['tasks', 'careTask']));
                onClose();
            } else {
                handleError(
                    error,
                    undefined,
                    careTaskRequestData.plannedCareTasksResolveTask?.map((task) => task.id)
                );
                if (autoSubmit) hasPreviousDrawer ? handleGoBack() : onClose();
            }
        }
    };

    const handleSubmit = (e: SyntheticEvent) => {
        e.preventDefault();

        const observationDetails = Object.entries(observationValues ?? {}).flatMap(
            ([observation, descriptors]) => descriptors.split(',').map((d) => `${observation}:${d}`)
        );

        trackEvent('Submit Button Clicked', {
            objectType: ObjectTypes.Button,
            buttonLabel: submitButtonLabel,
            isCareRefusal,
            context: analyticsPrefix,
            taskId: task?.id,
            isEmergency,
            residentId: residentToUse?.id || residentId,
            notesLength: notes?.length || 0,
            numberOfStaffAssisting: counter,
            observations,
            observationDetails,
            observationDetailsCount: observationDetails.length,
        });

        if (isLoggingCare) {
            addResidentActivity();
        } else {
            handleResolveTask();
        }
    };

    useEffect(() => {
        if (isTrainingAlert && isGuidedTour) {
            const timeout = setTimeout(() => {
                startNextStep(AlertTrainingTours.AlertTrainingAddNote);
            }, 500);

            return () => clearTimeout(timeout);
        }
    }, [isTrainingAlert, isGuidedTour, startNextStep]);

    useEffect(() => {
        trackDrawerView(title);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!autoSubmit) return;
        if (isLoggingCare) {
            addResidentActivity();
        } else {
            handleResolveTask();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (autoSubmit) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <NextStepViewport id={AlertTrainingViewportIds.AddNote}>
            <Box id={AlertTrainingElementIds.AddNote}>
                <DrawerTitle title={title} />

                <form onSubmit={handleSubmit}>
                    <DrawerBody allowShrink>
                        {shouldShowStaffAssisted && (
                            <>
                                <FormControl fullWidth>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 3 }}>
                                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                            # of staff who assisted
                                        </Typography>
                                        <Counter
                                            counter={counter}
                                            analyticsPrefix={analyticsPrefix}
                                            setCounter={setCounter}
                                        />
                                    </Stack>
                                </FormControl>
                                <Divider sx={{ my: spacing(2.5) }} />
                            </>
                        )}

                        <FormControl fullWidth sx={{ py: 0, gap: 1 }}>
                            <FormLabel htmlFor="notes">Add Notes</FormLabel>

                            <TextField
                                id="notes"
                                placeholder={
                                    shouldRequireNote
                                        ? 'Enter notes here'
                                        : shouldPromptForNote
                                        ? 'Describe the other care provided (optional)'
                                        : 'Enter notes here (optional)'
                                }
                                multiline
                                rows={4}
                                variant="outlined"
                                value={notes}
                                error={isOverCharacterLimit}
                                helperText={
                                    isOverCharacterLimit
                                        ? `${difference} characters over the limit`
                                        : `${difference} characters remaining`
                                }
                                onChange={({ target }) => setNotes(target.value)}
                                onFocus={() =>
                                    trackEvent('Notes Focused', {
                                        context: analyticsPrefix,
                                        objectType: ObjectTypes.Input,
                                    })
                                }
                                sx={{
                                    '& legend': {
                                        transition: 'unset',
                                    },
                                }}
                            />
                        </FormControl>
                    </DrawerBody>

                    <DrawerFooter sx={{ gap: 2 }}>
                        <Button
                            fullWidth
                            color="primary"
                            type="submit"
                            variant="contained"
                            disableFocusRipple
                            loading={shouldDisableActions}
                            disabled={
                                isOverCharacterLimit || (shouldRequireNote && notes.length === 0)
                            }
                            disableElevation
                        >
                            {submitButtonLabel}
                        </Button>

                        {showCancelAction && (
                            <Button
                                color="primary"
                                disableElevation
                                fullWidth
                                onClick={handleGoBack}
                            >
                                Go Back
                            </Button>
                        )}
                    </DrawerFooter>
                </form>
            </Box>
        </NextStepViewport>
    );
}

export default ResolveTaskDrawer;
